import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { User, Users } from "lucide-react";

/**
 * Shared recipient picker (Feb 2026 — unified). Used wherever an admin/
 * instructor needs to choose between contacting the Student or the Guardian:
 *   • Student Profile quick call/WhatsApp
 *   • Pickup QR WhatsApp share
 *   • Batch invite (Class Management)
 *   • Post-enrollment / post-renewal WhatsApp
 *   • Payment History receipt share
 *
 * Layout (single layout for every flow — Q2=a Student first):
 *   ┌────────────────────────────────────┐
 *   │ STUDENT                            │
 *   │   Aarav Mehta                      │
 *   │   9876543210                       │
 *   ├────────────────────────────────────┤
 *   │ PARENT/GUARDIAN                    │
 *   │   Rajesh Mehta                     │
 *   │   9876500000                       │
 *   └────────────────────────────────────┘
 *
 * Interaction: 1-step (Q1=a) — clicking a row fires onPick and closes.
 *
 * Auto-skip optimisation: when only one valid contact exists the caller
 * should fire the action directly without rendering this dialog.
 *
 * Props
 *   open, onOpenChange  — controlled visibility
 *   title               — dialog title (e.g. "Send WhatsApp invite?")
 *   description         — optional secondary line (e.g. "Who would you like to message?")
 *   options             — [{ key, role, name, phone }]
 *                          • key   "student" | "guardian"
 *                          • role  "Student" | "Parent/Guardian"
 *                          • name  person's actual name (or fallback label)
 *                          • phone raw phone string
 *   onPick(key)         — fires when user clicks an option
 */
export const ContactPickerDialog = ({
  open,
  onOpenChange,
  title = "Who would you like to contact?",
  description,
  options = [],
  onPick,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg" data-testid="contact-picker-title">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-slate-500">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {options.map((opt) => {
            const Icon = opt.key === "guardian" ? Users : User;
            // Backward-compat fallback: legacy callers passed {label, sublabel}.
            // Render those gracefully — `role` becomes the tag (or defaults
            // from `key`), `name` becomes `label`, `phone` becomes `sublabel`.
            const role = opt.role || (opt.key === "guardian" ? "Parent/Guardian" : "Student");
            const name = opt.name || opt.label || role;
            const phone = opt.phone || opt.sublabel || "";
            return (
              <button
                key={opt.key}
                type="button"
                disabled={opt.disabled}
                onClick={() => {
                  onPick?.(opt.key);
                  onOpenChange?.(false);
                }}
                // Dual test-ids — keep both naming conventions for backward
                // compatibility with existing tests and any callers that
                // grep for the legacy `recipient-picker-*` ids.
                data-testid={`contact-picker-${opt.key}`}
                data-recipient-picker={opt.key}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300 transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 bg-slate-100 text-slate-600">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {role}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 truncate mt-0.5">
                    {name}
                  </p>
                  <p className="text-xs text-slate-500 font-mono truncate">
                    {phone}
                  </p>
                </div>
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">
              No valid contact available.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPickerDialog;
